import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Copy, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingState } from "@/components/ui/loading-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResponsiveContainer } from "@/components/layout/responsive-grid";
import { useGroupTransactions } from "@/hooks/repository";
import { findDuplicateTransactions, type DuplicateConfidence } from "@/lib/transaction-duplicates";
import { formatCurrency } from "@/lib/utils";
import { useActiveGroup } from "@/stores/group.store";
import type { Transaction } from "@/api/schemas/transaction.schema";
import type { TransactionRecord } from "@/services/storage/storage.service";

type TypeFilter = "all" | Transaction["type"];
type ConfidenceFilter = "all" | DuplicateConfidence;

const typeLabels: Record<TypeFilter, string> = {
  all: "All types",
  expense: "Expenses",
  settlement: "Settlements",
  pool_event: "Pool events",
};

function formatTransactionDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function dateOnly(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function transactionPerson(transaction: Transaction) {
  return transaction.user?.name
    || transaction.from_user?.name
    || transaction.to_user?.name
    || "Unknown";
}

function transactionTypeLabel(transaction: Transaction) {
  if (transaction.type === "pool_event") {
    return transaction.event_type === "insert" ? "Pool insert" : "Pool expense";
  }
  return transaction.type === "expense" ? "Expense" : "Settlement";
}

function recordToTransaction(record: TransactionRecord): Transaction {
  const base = {
    id: record.id,
    type: record.type,
    description: record.description || null,
    amount: Number(record.amount || 0),
    currency: record.currency_code || "EUR",
    date: record.date || record.created_at || new Date().toISOString(),
    user: record.user,
  };

  if (record.type === "expense") {
    return {
      ...base,
      type: "expense",
      paid_by: record.paid_by,
      created_by: record.created_by,
      category: record.category
        ? {
          id: record.category.id,
          name: record.category.name,
          emoji: record.category.emoji || "",
          pinned: record.category.pinned,
        }
        : undefined,
    };
  }

  if (record.type === "settlement") {
    return {
      ...base,
      type: "settlement",
      kind: record.kind,
      origin_event_id: record.origin_event_id,
      from_user: record.from_user || record.user,
      to_user: record.to_user,
    };
  }

  return {
    ...base,
    type: "pool_event",
    event_type: record.event_type,
    pool_id: record.pool_id,
    pool_name: record.pool_name,
    expense_id: record.expense_id,
  };
}

function confidenceClass(confidence: DuplicateConfidence) {
  return confidence === "high"
    ? "border-amber-200 bg-amber-50 text-amber-900"
    : "border-slate-200 bg-slate-50 text-slate-700";
}

function withinDateRange(transaction: Transaction, from: string, to: string) {
  const value = dateOnly(transaction.date);
  if (!value) return false;
  if (from && value < from) return false;
  if (to && value > to) return false;
  return true;
}

export const ExpenseDuplicatesPage: React.FC = () => {
  const { activeGroupId } = useActiveGroup();
  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>("all");
  const [confidenceFilter, setConfidenceFilter] = React.useState<ConfidenceFilter>("all");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");

  const {
    data: transactionRecords,
    isLoading,
    error,
  } = useGroupTransactions(activeGroupId || "", { enabled: !!activeGroupId });

  const transactions = React.useMemo(
    () => (transactionRecords ?? [])
      .filter((record) => !record.deleted)
      .map(recordToTransaction),
    [transactionRecords]
  );

  const filteredTransactions = React.useMemo(
    () => transactions.filter((transaction) => {
      if (typeFilter !== "all" && transaction.type !== typeFilter) return false;
      return withinDateRange(transaction, dateFrom, dateTo);
    }),
    [dateFrom, dateTo, transactions, typeFilter]
  );

  const duplicateGroups = React.useMemo(
    () => findDuplicateTransactions(filteredTransactions),
    [filteredTransactions]
  );

  const visibleGroups = React.useMemo(
    () => duplicateGroups.filter((group) =>
      confidenceFilter === "all" ? true : group.confidence === confidenceFilter
    ),
    [confidenceFilter, duplicateGroups]
  );

  const involvedTransactionCount = React.useMemo(
    () => new Set(visibleGroups.flatMap((group) => group.transactions.map((transaction) => transaction.id))).size,
    [visibleGroups]
  );

  if (!activeGroupId) {
    return (
      <ResponsiveContainer size="xl">
        <div className="flex min-h-[400px] items-center justify-center text-center">
          <div>
            <h1 className="text-2xl font-bold">Duplicates</h1>
            <p className="mt-2 text-sm text-muted-foreground">Select a group to review duplicate transactions.</p>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer size="xl">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">Duplicates</h1>
            <p className="mt-1 text-sm text-muted-foreground">Existing transactions with near dates, near amounts, and matching labels.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <RouterLink to="/expenses">Transactions</RouterLink>
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()} disabled={isLoading}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs font-medium uppercase text-muted-foreground">Groups</div>
              <div className="mt-2 text-2xl font-semibold">{visibleGroups.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs font-medium uppercase text-muted-foreground">Transactions</div>
              <div className="mt-2 text-2xl font-semibold">{involvedTransactionCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs font-medium uppercase text-muted-foreground">Loaded</div>
              <div className="mt-2 text-2xl font-semibold">{transactions.length}</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-3 rounded-md border bg-card p-3 sm:grid-cols-[minmax(160px,1fr)_minmax(160px,1fr)_minmax(130px,0.8fr)_minmax(130px,0.8fr)]">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as TypeFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{typeLabels.all}</SelectItem>
                <SelectItem value="expense">{typeLabels.expense}</SelectItem>
                <SelectItem value="settlement">{typeLabels.settlement}</SelectItem>
                <SelectItem value="pool_event">{typeLabels.pool_event}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Confidence</Label>
            <Select value={confidenceFilter} onValueChange={(value) => setConfidenceFilter(value as ConfidenceFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All confidence</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="duplicates-from">From</Label>
            <Input id="duplicates-from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="duplicates-to">To</Label>
            <Input id="duplicates-to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </div>
        </div>

        {isLoading && transactions.length === 0 ? (
          <LoadingState message="Loading transactions..." />
        ) : error ? (
          <div className="flex min-h-[280px] items-center justify-center rounded-md border border-dashed text-center">
            <div>
              <Copy className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <h2 className="font-semibold">Failed to load transactions</h2>
            </div>
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="flex min-h-[280px] items-center justify-center rounded-md border border-dashed text-center">
            <div>
              <Copy className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <h2 className="font-semibold">No duplicates found</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                The current filters did not produce duplicate candidates.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleGroups.map((group) => (
              <Card key={group.id}>
                <CardContent className="p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={confidenceClass(group.confidence)}>
                          {group.confidence === "high" ? "High confidence" : "Medium confidence"}
                        </Badge>
                        <Badge variant="secondary">{typeLabels[group.type]}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {group.transactions.length} transactions
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {group.reasons.map((reason) => (
                          <Badge key={reason} variant="outline">{reason}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground sm:text-right">
                      <div>{group.dateSpanDays} day{group.dateSpanDays === 1 ? "" : "s"} span</div>
                      <div>{formatCurrency(group.amountSpan, group.transactions[0]?.currency || "EUR")} amount span</div>
                    </div>
                  </div>

                  <div className="mt-4 divide-y rounded-md border">
                    {group.transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="grid gap-2 p-3 sm:grid-cols-[120px_minmax(0,1fr)_160px_130px]"
                      >
                        <div className="text-sm text-muted-foreground">{formatTransactionDate(transaction.date)}</div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{transaction.description || transactionTypeLabel(transaction)}</div>
                          <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span>{transactionTypeLabel(transaction)}</span>
                            {transaction.category?.name && <span>{transaction.category.emoji} {transaction.category.name}</span>}
                          </div>
                        </div>
                        <div className="truncate text-sm text-muted-foreground">{transactionPerson(transaction)}</div>
                        <div className="text-sm font-semibold sm:text-right">
                          {formatCurrency(Math.abs(transaction.amount), transaction.currency || "EUR")}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ResponsiveContainer>
  );
};
